import React, { useState, useEffect, useCallback } from "react";
import { Coins, ArrowDownCircle, ArrowUpCircle, AlertTriangle, Wallet } from "lucide-react";
import {
  ResponsiveContainer, LineChart, Line, XAxis, YAxis, Tooltip, CartesianGrid, Legend,
} from "recharts";
import { Card, Spinner, PageHeader, Badge } from "../../lib/ui";
import { useT } from "../../lib/i18n";
import { useToast } from "../../lib/toast";
import api from "../../lib/api";
import { rp, PeriodPicker, BranchFilter, KpiCard } from "../../components/finance";

export default function CashFlow() {
  const { t } = useT();
  const toast = useToast();
  const [period, setPeriod] = useState(() => { const d = new Date(); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`; });
  const [branchId, setBranchId] = useState("");
  const [d, setD] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try { const { data } = await api.get("/finance/cash-flow", { params: { period, branch_id: branchId || undefined } }); setD(data); }
    catch { toast.error(t("Failed to load")); }
    finally { setLoading(false); }
  }, [period, branchId, t, toast]);
  useEffect(() => { load(); }, [load]);

  // merge scenario series into one dataset for the chart
  const chartData = d ? d.scenarios.base.series.map((row, i) => ({
    month: `M${row.month}`,
    worst: d.scenarios.worst.series[i].saldo_akhir,
    base: row.saldo_akhir,
    best: d.scenarios.best.series[i].saldo_akhir,
  })) : [];

  return (
    <div data-testid="finance-cashflow-page">
      <PageHeader title={t("Cash Flow")} subtitle={t("Cash in/out, runway & 12-month projection")} icon={Coins}>
        <BranchFilter value={branchId} onChange={setBranchId} />
        <PeriodPicker value={period} onChange={setPeriod} />
      </PageHeader>

      {loading || !d ? <div className="flex h-64 items-center justify-center"><Spinner className="h-8 w-8" /></div> : (
        <div className="space-y-5">
          {d.current_cash_is_estimate && (
            <div className="flex items-center gap-2 rounded-xl bg-blue-50 px-4 py-2.5 text-xs text-blue-800" data-testid="finance-cf-estimate-note">
              <Wallet size={14} className="shrink-0" /> {t("Branch")}: <b>{d.branch_name}</b> · {t("Cash Position & Runway are revenue-share estimates (no per-branch bank balance).")}
            </div>
          )}
          {d.alerts?.length > 0 && (
            <div className="space-y-2" data-testid="finance-cf-alerts">
              {d.alerts.map((a, i) => (
                <div key={i} className={`flex items-center gap-2 rounded-xl px-4 py-2.5 text-sm ${a.severity === "RED" ? "bg-danger-light text-danger-text" : "bg-warn-light text-warn-text"}`}>
                  <AlertTriangle size={16} className="shrink-0" /> {a.message}
                </div>
              ))}
            </div>
          )}

          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            <KpiCard icon={ArrowDownCircle} tone="success" label={t("Cash In")} value={rp(d.cash_in)} sub={`${t("Sales")} ${rp(d.auto_sales_in)}`} testid="finance-cf-in" />
            <KpiCard icon={ArrowUpCircle} tone="danger" label={t("Cash Out")} value={rp(d.cash_out)} sub={`${t("Supplier")} ${rp(d.auto_supplier_out)}`} testid="finance-cf-out" />
            <KpiCard icon={Coins} tone={d.net_cash_flow >= 0 ? "success" : "danger"} label={t("Net Cash Flow")} value={rp(d.net_cash_flow)} testid="finance-cf-net" />
            <KpiCard icon={Wallet} tone="info" label={d.current_cash_is_estimate ? `${t("Cash Position")} (${t("Estimasi")})` : `${t("Cash Position")} · GL`} value={rp(d.current_cash)} sub={d.runway_months != null ? `${t("runway")} ${d.runway_months} ${t("months")}${d.runway_is_estimate ? ` · ${t("Estimasi")}` : ""}` : null} testid="finance-cf-cash" />
          </div>

          {/* MJ-02: GL-derived cash reconciliation */}
          {d.gl_cash_accounts && (
            <Card data-testid="finance-cf-gl-cash">
              <h3 className="mb-2 font-heading font-bold text-ink">{t("Cash Position from General Ledger")}</h3>
              <div className="space-y-1.5 text-sm">
                {d.gl_cash_accounts.map((a) => (
                  <div key={a.account_code} className="flex justify-between" data-testid={`finance-cf-gl-${a.account_code}`}>
                    <span className="text-ink-soft">{a.account_code} · {a.account_name}</span>
                    <span className="font-medium text-ink">{rp(a.amount)}</span>
                  </div>
                ))}
                <div className="flex justify-between border-t border-line pt-2 font-bold">
                  <span className="text-ink">{t("Total (GL)")}</span><span className="text-ink" data-testid="finance-cf-gl-total">{rp(d.company_cash)}</span>
                </div>
                <div className="flex justify-between text-xs text-ink-mute">
                  <span>{t("Manual configuration value")}</span><span data-testid="finance-cf-manual-cash">{rp(d.manual_cash)}</span>
                </div>
                <div className="flex justify-between text-xs">
                  <span className="text-ink-mute">{t("Variance (GL − manual)")}</span>
                  <span className={Math.abs(d.cash_variance || 0) < 1 ? "font-semibold text-success-text" : "font-semibold text-warn-text"} data-testid="finance-cf-cash-variance">{rp(d.cash_variance)}</span>
                </div>
              </div>
            </Card>
          )}

          {/* Breakdown */}
          <div className="grid gap-4 lg:grid-cols-3">
            <Card data-testid="finance-cf-breakdown">
              <h3 className="mb-3 font-heading font-bold text-ink">{t("This Month")}</h3>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between"><span className="text-ink-soft">{t("Sales (auto)")}</span><span className="font-medium text-success-text">+{rp(d.auto_sales_in)}</span></div>
                <div className="flex justify-between"><span className="text-ink-soft">{t("Manual cash in")}</span><span className="font-medium text-success-text">+{rp(d.manual_cash_in)}</span></div>
                <div className="flex justify-between"><span className="text-ink-soft">{t("Supplier payments (auto)")}</span><span className="font-medium text-danger-text">-{rp(d.auto_supplier_out)}</span></div>
                <div className="flex justify-between"><span className="text-ink-soft">{t("Manual cash out")}</span><span className="font-medium text-danger-text">-{rp(d.manual_cash_out)}</span></div>
                <div className="flex justify-between border-t border-line pt-2 font-bold"><span className="text-ink">{t("Net Cash Flow")}</span><span className={d.net_cash_flow >= 0 ? "text-success-text" : "text-danger-text"}>{rp(d.net_cash_flow)}</span></div>
              </div>
            </Card>

            {/* Projection chart */}
            <Card className="lg:col-span-2" data-testid="finance-cf-projection">
              <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
                <h3 className="font-heading font-bold text-ink">{t("12-Month Cash Projection")}</h3>
                <div className="flex gap-2 text-xs">
                  <Badge tone="danger">{t("Worst")}: {rp(d.scenarios.worst.ending_balance)}</Badge>
                  <Badge tone="info">{t("Base")}: {rp(d.scenarios.base.ending_balance)}</Badge>
                  <Badge tone="success">{t("Best")}: {rp(d.scenarios.best.ending_balance)}</Badge>
                </div>
              </div>
              <div className="h-60">
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={chartData} margin={{ top: 5, right: 10, left: 0, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e7e5e4" />
                    <XAxis dataKey="month" tick={{ fontSize: 11 }} />
                    <YAxis tick={{ fontSize: 10 }} tickFormatter={(v) => `${(v / 1e6).toFixed(0)}jt`} />
                    <Tooltip formatter={(v) => rp(v)} />
                    <Legend wrapperStyle={{ fontSize: 11 }} />
                    <Line type="monotone" dataKey="worst" stroke="#ef4444" strokeWidth={2} dot={false} name={t("Worst")} />
                    <Line type="monotone" dataKey="base" stroke="#3b82f6" strokeWidth={2} dot={false} name={t("Base")} />
                    <Line type="monotone" dataKey="best" stroke="#22c55e" strokeWidth={2} dot={false} name={t("Best")} />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            </Card>
          </div>
        </div>
      )}
    </div>
  );
}
